import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import { BrowserRouter as Router } from "react-router-dom";
import App from './App.jsx'
// Resolved to posthog-js's slim build by the alias in vite.config.js: same
// init/capture/pageview behavior, but optional extensions (session replay,
// surveys, autocapture extras) load from the PostHog CDN on demand instead
// of shipping ~32 KB gzip in the main chunk every visitor downloads.
import posthog from 'posthog-js';
import { PostHogProvider } from '@posthog/react';
import { dropNoiseExceptions } from './lib/telemetryFilter.js';

// Dev and emulator sessions stay out of telemetry entirely: the dev server
// mid-edit throws HMR ReferenceErrors, and test-account replays land in the
// production project, burying the real errors staff act on. Uninitialized,
// every capture call is a no-op, so nothing downstream needs a guard.
if (import.meta.env.PROD && import.meta.env.VITE_USE_EMULATORS !== "true") {
  posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
    defaults: '2026-05-30',
    before_send: dropNoiseExceptions,
    session_recording: {blockSelector: ".ph-no-capture"},
  });
}

// The emulator test hooks (window.__steelhacksTestSignIn and friends) are
// installed by src/firebase.js as a side effect of loading. With firebase
// deferred behind the session flag, an anonymous page never loads it, and
// the e2e suite's sign-in helper hangs waiting for the hook. Load it eagerly
// in emulator mode only — the condition is baked at build time, so
// production bundles keep the deferral.
if (import.meta.env.VITE_USE_EMULATORS === "true") {
  import('./firebase.js');
}

// After a deploy, open tabs still reference the previous build's hashed
// asset names; when Vite fails to preload one, refresh to pick up the new
// build instead of crashing the route. lazyWithRetry handles the failed
// dynamic imports themselves — this covers their preloaded dependencies.
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  window.location.reload();
});

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <PostHogProvider client={posthog}>
      <Router>
        <App />
      </Router>
    </PostHogProvider>
  </StrictMode>,
)
